import type { PopoverContentProps as RadixPopoverContentProps } from '@radix-ui/react-popover'
import type { BodyProps, ContentPosition, PopoverContentProps } from './Popover.types'
import * as RadixPopover from '@radix-ui/react-popover'
import cn from 'classnames'
import * as React from 'react'
import backdropStyles from '../overlays/backdrop.module.css'
import { TOP_TITLE_BAR_HEIGHT, TopTitleBar } from '../top-title-bar'
import { Typography } from '../typography'
import styles from './Popover.module.css'

const PositionMap: Record<ContentPosition, Pick<RadixPopoverContentProps, 'side' | 'align'>> = {
  'top': { side: 'top', align: 'center' },
  'bottom': { side: 'bottom', align: 'center' },
  'left': { side: 'left', align: 'center' },
  'right': { side: 'right', align: 'center' },
  'top-left': { side: 'top', align: 'end' },
  'top-right': { side: 'top', align: 'start' },
  'bottom-left': { side: 'bottom', align: 'end' },
  'bottom-right': { side: 'bottom', align: 'start' },
}

export function Root({ modal = false, ...props }: RadixPopover.PopoverProps) {
  return <RadixPopover.Root {...props} modal={modal} />
}

export function Trigger({ children, ...props }: RadixPopover.PopoverTriggerProps) {
  return (
    <RadixPopover.Trigger asChild {...props} onMouseDown={e => e.preventDefault()}>
      {children}
    </RadixPopover.Trigger>
  )
}

export function Body({
  className,
  hasPadding = true,
  hasBodyOverflow = true,
  children,
}: BodyProps) {
  return (
    <div
      data-qa="popoverBody"
      className={cn(
        styles.body,
        {
          [styles.bodyNoPadding]: !hasPadding,
          [styles.bodyNoOverflow]: !hasBodyOverflow,
        },
        className,
      )}
    >
      {children}
    </div>
  )
}

function Content({
  children,
  showTitleOnDesktop = false,
  title,
  position = 'bottom',
  fullHeight = false,
  className,
  closeText,
  autoFocus = false,
  hasBodyOverflow = true,
  hasPadding = true,
  ...rest
}: React.PropsWithChildren<PopoverContentProps>) {
  const { side, align } = PositionMap[position]

  return (
    <RadixPopover.Portal>
      <div className={cn(styles.wrapper, { [styles.fullHeight]: fullHeight })}>
        <RadixPopover.Close className={cn(backdropStyles.backdrop, styles.backdrop)} />
        <RadixPopover.Content
          onOpenAutoFocus={(event) => {
            if (!autoFocus) {
              event.preventDefault()
            }
          }}
          side={side}
          align={align}
          collisionPadding={8}
          className={cn(styles.content, className)}
          {...rest}
        >
          <aside className={styles.surface} tabIndex={-1}>
            <TopTitleBar.Root
              className={cn(styles.topTitleBar, { [styles.hideTitleOnDesktop]: !showTitleOnDesktop })}
              style={{
                minHeight: TOP_TITLE_BAR_HEIGHT,
                height: TOP_TITLE_BAR_HEIGHT,
              }}
            >
              <TopTitleBar.CenterArea title={title} />
              <TopTitleBar.RightArea>
                <RadixPopover.Close>
                  <Typography>{closeText}</Typography>
                </RadixPopover.Close>
              </TopTitleBar.RightArea>
            </TopTitleBar.Root>
            <Body hasPadding={hasPadding} hasBodyOverflow={hasBodyOverflow}>
              {children}
            </Body>
          </aside>
        </RadixPopover.Content>
      </div>
    </RadixPopover.Portal>
  )
}

export const Popover = {
  Root,
  Trigger,
  Content,
  Body,
}
